import type { ComponentChildren, JSX } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import {
  addAttachments,
  closeComposerSafe,
  discardComposer,
  discardDraft,
  queueSend,
  removeAttachment,
  saveAndCloseComposer,
  saveDraft,
  updateComposer,
} from "../actions";
import { api } from "../api";
import {
  composer,
  composerAttachments,
  composerDraftUid,
  composerError,
  composerLastSaved,
  composerPendingSend,
  composerSaving,
  composerSending,
  composerSession,
  composerShowConfirm,
  composerWindow,
  defaultDomain,
  messages,
  userEmail,
  userSettings,
} from "../state";
import { t } from "../i18n";
import type { Contact } from "../../shared/types";
import { fileKind, formatBytes, joinRecipients, parseRecipient, splitRecipients, type Recipient } from "../util";
import { Icon, type IconName } from "./icons";
import { Avatar, DESKTOP, Dialog, Spinner, useMedia } from "./kit";

const TITLE_KEYS = {
  new: "compose.new",
  reply: "compose.reply",
  replyAll: "compose.replyAll",
  forward: "compose.forward",
  draft: "compose.draft",
} as const;

export function Composer(): JSX.Element | null {
  const draft = composer.value;
  const desktop = useMedia(DESKTOP);
  if (!draft || composerPendingSend.value) return null;

  const mode = desktop ? composerWindow.value : "maximized";
  const title = draft.subject.trim() || t(TITLE_KEYS[draft.mode]);

  if (mode === "minimized") {
    return (
      <div class="float-panel animate-rise fixed bottom-0 right-6 z-50 w-[320px] !rounded-b-none">
        <TitleBar title={title} />
      </div>
    );
  }

  const panel = (
    <ComposerPanel
      class={
        mode === "maximized"
          ? desktop
            ? "h-[88vh] w-[min(980px,94vw)]"
            : "h-full w-full !rounded-none !border-0"
          : "h-[min(660px,calc(100vh-48px))] w-[560px] !rounded-b-none"
      }
      title={title}
    />
  );

  if (mode === "maximized") {
    return (
      <div
        class="animate-fade fixed inset-0 z-50 flex items-center justify-center bg-scrim md:p-6"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) composerWindow.value = "normal";
        }}
      >
        {panel}
      </div>
    );
  }
  return <div class="fixed bottom-0 right-6 z-50">{panel}</div>;
}

function TitleBar({ title }: { title: string }): JSX.Element {
  const desktop = useMedia(DESKTOP);
  const win = composerWindow.value;
  return (
    <div
      class="flex h-11 shrink-0 cursor-default items-center gap-1 border-b border-line bg-canvas pl-4 pr-1.5"
      onDblClick={() => desktop && (composerWindow.value = win === "minimized" ? "normal" : "minimized")}
    >
      <span class="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-fg">{title}</span>
      <SaveStatus />
      {desktop && (
        <>
          <button
            class="icon-btn icon-btn-sm"
            title={win === "minimized" ? t("compose.restore") : t("compose.minimize")}
            aria-label={win === "minimized" ? t("compose.restore") : t("compose.minimize")}
            onClick={() => (composerWindow.value = win === "minimized" ? "normal" : "minimized")}
          >
            <Icon name={win === "minimized" ? "chevronUp" : "minus"} size={16} />
          </button>
          <button
            class="icon-btn icon-btn-sm"
            title={win === "maximized" ? t("compose.dock") : t("compose.maximize")}
            aria-label={win === "maximized" ? t("compose.dock") : t("compose.maximize")}
            onClick={() => (composerWindow.value = win === "maximized" ? "normal" : "maximized")}
          >
            <Icon name={win === "maximized" ? "minimize" : "maximize"} size={14} />
          </button>
        </>
      )}
      <button class="icon-btn icon-btn-sm" title={t("common.close")} aria-label={t("common.close")} onClick={() => closeComposerSafe()}>
        <Icon name="x" size={17} />
      </button>
    </div>
  );
}

function SaveStatus(): JSX.Element | null {
  if (composerSaving.value) {
    return <span class="mr-1 hidden text-xs text-fg-3 sm:inline">{t("compose.saving")}</span>;
  }
  if (composerLastSaved.value && composerDraftUid.value) {
    return <span class="mr-1 hidden text-xs text-fg-3 sm:inline">{t("compose.saved")}</span>;
  }
  return null;
}

function ComposerPanel({ class: cls, title }: { class: string; title: string }): JSX.Element {
  const draft = composer.value!;
  const bodyRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const sending = composerSending.value;
  const err = composerError.value;
  const attachments = composerAttachments.value;
  const session = composerSession.value;
  const fromName = userSettings.value.displayName;

  // (Re)initialize the editor each time a composer session starts.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.innerHTML = draft.body.includes("<") ? draft.body : draft.body.replace(/\n/g, "<br>");
    if (draft.mode === "reply" || draft.mode === "replyAll") {
      el.focus();
      // Caret at the very start, above the quote.
      const range = document.createRange();
      range.setStart(el, 0);
      range.collapse(true);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }, [session]);

  function syncBody(): void {
    if (bodyRef.current) updateComposer({ body: bodyRef.current.innerHTML });
  }

  function exec(cmd: string, value?: string): void {
    bodyRef.current?.focus();
    document.execCommand(cmd, false, value);
    syncBody();
  }

  function insertLink(): void {
    const url = window.prompt(t("compose.linkPrompt"), "https://");
    if (url && /^(https?:|mailto:)/i.test(url.trim())) exec("createLink", url.trim());
  }

  return (
    <div
      class={`float-panel animate-rise relative flex flex-col overflow-hidden ${cls}`}
      role="dialog"
      aria-label={title}
      onKeyDown={(e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();
          void queueSend();
        }
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
          e.preventDefault();
          void saveDraft();
        }
      }}
      onDragOver={(e) => {
        if (e.dataTransfer?.types.includes("Files")) {
          e.preventDefault();
          setDragging(true);
        }
      }}
      onDragLeave={(e) => {
        if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setDragging(false);
      }}
      onDrop={(e) => {
        if (!e.dataTransfer?.files.length) return;
        e.preventDefault();
        setDragging(false);
        void addAttachments([...e.dataTransfer.files]);
      }}
    >
      <TitleBar title={title} />

      <div class="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <FieldRow label={t("compose.from")}>
          <div class="flex min-w-0 items-center gap-2 py-2 text-[13px] text-fg-2">
            <Avatar address={userEmail.value} label={fromName || userEmail.value} size={20} />
            <span class="truncate">
              {fromName ? (
                <>
                  {fromName} <span class="text-fg-3">&lt;{userEmail.value}&gt;</span>
                </>
              ) : (
                userEmail.value
              )}
            </span>
          </div>
        </FieldRow>
        <FieldRow label={t("compose.to")}>
          <RecipientField
            value={draft.to}
            autofocus={draft.mode === "new" || draft.mode === "forward"}
            onValue={(v) => updateComposer({ to: v })}
            label={t("compose.to")}
          />
          {!draft.showCc && (
            <button class="shrink-0 rounded px-1.5 py-1 text-xs font-medium text-fg-3 hover:bg-raised hover:text-fg" onClick={() => updateComposer({ showCc: true })}>
              Cc Bcc
            </button>
          )}
        </FieldRow>
        {draft.showCc && (
          <>
            <FieldRow label="Cc">
              <RecipientField value={draft.cc} onValue={(v) => updateComposer({ cc: v })} label="Cc" />
            </FieldRow>
            <FieldRow label="Bcc">
              <RecipientField value={draft.bcc} onValue={(v) => updateComposer({ bcc: v })} label="Bcc" />
            </FieldRow>
          </>
        )}
        <FieldRow label={t("compose.subject")}>
          <input
            class="w-full bg-transparent py-2.5 text-[14px] font-medium text-fg outline-none placeholder:font-normal placeholder:text-fg-4"
            value={draft.subject}
            placeholder={t("compose.subjectPlaceholder")}
            aria-label={t("compose.subject")}
            onInput={(e) => updateComposer({ subject: (e.target as HTMLInputElement).value })}
          />
        </FieldRow>

        <div
          ref={bodyRef}
          contentEditable
          role="textbox"
          aria-multiline="true"
          aria-label={t("compose.body")}
          data-placeholder={t("compose.bodyPlaceholder")}
          class="rich-body min-h-[180px] flex-1 px-5 py-4"
          onInput={syncBody}
          onPaste={(e) => {
            const files = [...(e.clipboardData?.files ?? [])];
            if (files.length) {
              e.preventDefault();
              void addAttachments(files);
            }
          }}
        />

        {attachments.length > 0 && (
          <ul class="flex flex-wrap gap-2 px-5 pb-4">
            {attachments.map((a) => (
              <li key={a.id} class="flex max-w-[240px] items-center gap-2 rounded-[8px] border border-line bg-canvas py-1.5 pl-2.5 pr-1 text-xs">
                {a.content === null ? <Spinner size={13} /> : <Icon name={fileKind(a.contentType, a.filename) === "image" ? "image" : "file"} size={14} class="shrink-0 text-fg-3" />}
                <span class="min-w-0 flex-1 truncate text-fg" title={a.filename}>
                  {a.filename}
                </span>
                <span class="shrink-0 text-fg-3">{formatBytes(a.size)}</span>
                <button class="icon-btn h-6 w-6" aria-label={t("compose.removeAttachment", { name: a.filename })} onClick={() => removeAttachment(a.id)}>
                  <Icon name="x" size={13} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {err && (
        <div class="flex items-center gap-2 border-t border-danger/30 bg-danger/5 px-5 py-2 text-[13px] text-danger" role="alert">
          <Icon name="alert" size={15} />
          {err}
        </div>
      )}

      <div class="flex shrink-0 items-center gap-1 border-t border-line px-3 py-2.5">
        <button class="btn btn-primary !px-4" disabled={sending} onClick={() => void queueSend()} title={`${t("compose.send")} (Ctrl+Enter)`}>
          {sending ? <Spinner size={14} class="!border-on-accent/40 !border-t-on-accent" /> : <Icon name="send" size={15} />}
          {sending ? t("compose.sending") : t("compose.send")}
        </button>
        <div class="ml-1 flex items-center">
          <FormatButton icon="bold" label={t("format.bold")} onClick={() => exec("bold")} />
          <FormatButton icon="italic" label={t("format.italic")} onClick={() => exec("italic")} />
          <FormatButton icon="underline" label={t("format.underline")} onClick={() => exec("underline")} class="hidden sm:inline-flex" />
          <FormatButton icon="list" label={t("format.bullets")} onClick={() => exec("insertUnorderedList")} class="hidden sm:inline-flex" />
          <FormatButton icon="listOrdered" label={t("format.numbers")} onClick={() => exec("insertOrderedList")} class="hidden md:inline-flex" />
          <FormatButton icon="quote" label={t("format.quote")} onClick={() => exec("formatBlock", "blockquote")} class="hidden md:inline-flex" />
          <FormatButton icon="link" label={t("format.link")} onClick={insertLink} />
          <FormatButton icon="eraser" label={t("format.clear")} onClick={() => exec("removeFormat")} class="hidden md:inline-flex" />
          <span class="mx-1 h-5 w-px bg-line" />
          <FormatButton icon="paperclip" label={t("compose.attach")} onClick={() => fileRef.current?.click()} />
          <input
            ref={fileRef}
            type="file"
            multiple
            class="hidden"
            onChange={(e) => {
              const input = e.currentTarget as HTMLInputElement;
              void addAttachments([...(input.files ?? [])]);
              input.value = "";
            }}
          />
        </div>
        <button
          class="icon-btn ml-auto"
          title={t("compose.discard")}
          aria-label={t("compose.discard")}
          onClick={() => void discardDraft()}
        >
          <Icon name="trash" size={17} />
        </button>
      </div>

      {dragging && (
        <div class="pointer-events-none absolute inset-0 z-10 m-2 flex items-center justify-center rounded-[10px] border-2 border-dashed border-accent bg-accent/8 text-[14px] font-medium text-accent-fg">
          <Icon name="paperclip" size={18} class="mr-2" />
          {t("compose.dropFiles")}
        </div>
      )}

      {composerShowConfirm.value && (
        <Dialog onClose={() => (composerShowConfirm.value = false)} label={t("compose.unsavedTitle")} class="max-w-sm">
          <div class="px-5 pb-4 pt-5">
            <h2 class="text-[15px] font-semibold text-fg">{t("compose.unsavedTitle")}</h2>
            <p class="mt-1.5 text-[13px] text-fg-3">{t("compose.unsavedBody")}</p>
          </div>
          <div class="flex justify-end gap-2 border-t border-line px-5 py-3">
            <button class="btn btn-ghost" onClick={() => discardComposer()}>
              {t("compose.discard")}
            </button>
            <button class="btn btn-primary" autofocus onClick={() => void saveAndCloseComposer()}>
              {t("compose.saveClose")}
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

function FormatButton({ icon, label, onClick, class: cls = "" }: { icon: IconName; label: string; onClick: () => void; class?: string }): JSX.Element {
  return (
    <button
      type="button"
      class={`icon-btn icon-btn-sm ${cls}`}
      title={label}
      aria-label={label}
      // Keep the editor's selection: act on mousedown and prevent focus moving.
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      <Icon name={icon} size={16} />
    </button>
  );
}

function FieldRow({ label, children }: { label: string; children: ComponentChildren }): JSX.Element {
  return (
    <div class="flex items-center gap-2 border-b border-line px-5">
      <span class="w-12 shrink-0 text-[13px] text-fg-3">{label}</span>
      {children}
    </div>
  );
}

/* ---------------------------- recipient chips ---------------------------- */

const contactCache = new Map<string, Contact[]>();

/** Addresses seen in the loaded list, so autocomplete works before the address book fills up. */
function localContacts(q: string): Contact[] {
  const seen = new Map<string, Contact>();
  for (const m of messages.value) {
    for (const a of [...m.from, ...m.to]) {
      const key = a.address.toLowerCase();
      if (!key || key === userEmail.value.toLowerCase() || /no-?reply|mailer-daemon/.test(key)) continue;
      if (!key.includes(q) && !a.name.toLowerCase().includes(q)) continue;
      const cur = seen.get(key);
      seen.set(key, { address: key, name: cur?.name || a.name, count: (cur?.count ?? 0) + 1 });
    }
  }
  return [...seen.values()].sort((a, b) => b.count - a.count);
}

function RecipientField({
  value,
  autofocus,
  onValue,
  label,
}: {
  value: string;
  autofocus?: boolean;
  onValue: (v: string) => void;
  label: string;
}): JSX.Element {
  const chips = splitRecipients(value);
  const [input, setInput] = useState("");
  const [suggest, setSuggest] = useState<Contact[]>([]);
  const [active, setActive] = useState(0);
  // Escape hides suggestions until the user types again.
  const [dismissed, setDismissed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const domain = defaultDomain.value;

  useEffect(() => {
    if (autofocus) inputRef.current?.focus();
  }, []);

  // Contact lookup: local harvest instantly, then the server address book.
  useEffect(() => {
    setDismissed(false);
    const q = input.trim().toLowerCase();
    if (!q) {
      setSuggest([]);
      return;
    }
    const taken = new Set(chips.map((c) => c.address.toLowerCase()));
    const merge = (server: Contact[]): void => {
      const all = new Map<string, Contact>();
      for (const c of [...server, ...localContacts(q)]) if (!all.has(c.address) && !taken.has(c.address)) all.set(c.address, c);
      setSuggest([...all.values()].slice(0, 6));
      setActive(0);
    };
    merge(contactCache.get(q) ?? []);
    const timer = setTimeout(() => {
      void api
        .contacts(q)
        .then((list) => {
          contactCache.set(q, list);
          merge(list);
        })
        .catch(() => {});
    }, 140);
    return () => clearTimeout(timer);
  }, [input]);

  // Default-domain completion: "ana" → "ana@domain", "ana@" → "ana@domain".
  const domainOption =
    domain && input.trim() && !input.includes(" ") && (!input.includes("@") || input.endsWith("@"))
      ? `${input.trim().replace(/@$/, "")}@${domain}`
      : null;
  const options: Array<{ raw: string; name: string; address: string }> = dismissed ? [] : [
    ...(domainOption && !suggest.some((s) => s.address === domainOption) ? [{ raw: domainOption, name: "", address: domainOption }] : []),
    ...suggest.map((c) => ({ raw: c.name ? `${c.name} <${c.address}>` : c.address, name: c.name, address: c.address })),
  ];

  function commit(raw: string): void {
    const text = raw.trim().replace(/[,;]+$/, "");
    if (!text) return;
    const added = splitRecipients(text);
    onValue(joinRecipients([...chips, ...added]));
    setInput("");
    setSuggest([]);
  }

  function removeAt(i: number): void {
    onValue(joinRecipients(chips.filter((_, j) => j !== i)));
  }

  return (
    <div class="relative min-w-0 flex-1">
      <div class="flex flex-wrap items-center gap-1 py-1.5" onClick={() => inputRef.current?.focus()}>
        {chips.map((c, i) => (
          <Chip
            key={`${c.raw}-${i}`}
            r={c}
            onRemove={() => removeAt(i)}
            onEdit={() => {
              removeAt(i);
              setInput(c.raw);
              inputRef.current?.focus();
            }}
          />
        ))}
        <input
          ref={inputRef}
          class="h-7 min-w-[120px] flex-1 bg-transparent text-[14px] text-fg outline-none placeholder:text-fg-4"
          value={input}
          aria-label={label}
          aria-autocomplete="list"
          aria-expanded={options.length > 0}
          placeholder={chips.length ? "" : t("compose.recipientPlaceholder")}
          onInput={(e) => {
            const v = (e.currentTarget as HTMLInputElement).value;
            // Typing or pasting a separator commits what came before it.
            if (/[,;]/.test(v)) {
              const parts = v.split(/[,;]/);
              const rest = parts.pop() ?? "";
              commit(parts.join(","));
              setInput(rest.trimStart());
            } else setInput(v);
          }}
          onKeyDown={(e) => {
            if (options.length && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
              e.preventDefault();
              setActive((a) => (a + (e.key === "ArrowDown" ? 1 : -1) + options.length) % options.length);
            } else if (e.key === "Enter" || (e.key === "Tab" && input.trim())) {
              if (!input.trim()) return;
              e.preventDefault();
              commit(options[active]?.raw ?? input);
            } else if (e.key === "Backspace" && !input && chips.length) {
              removeAt(chips.length - 1);
            } else if (e.key === "Escape" && options.length) {
              e.stopPropagation();
              setDismissed(true);
            }
          }}
          onBlur={() => {
            // Let a click on a suggestion land before committing raw text.
            setTimeout(() => {
              if (document.activeElement !== inputRef.current && inputRef.current?.value.trim()) commit(inputRef.current.value);
              setSuggest([]);
            }, 120);
          }}
        />
      </div>
      {options.length > 0 && (
        <ul class="menu animate-fade absolute left-0 top-full z-30 w-[min(360px,100%)]" role="listbox">
          {options.map((o, i) => (
            <li key={o.address} role="option" aria-selected={i === active}>
              <button
                type="button"
                class="menu-item !h-auto py-1.5"
                data-active={i === active ? "true" : undefined}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(o.raw);
                }}
                onMouseEnter={() => setActive(i)}
              >
                <Avatar address={o.address} label={o.name || o.address} size={26} />
                <span class="min-w-0 flex-1">
                  {o.name && <span class="block truncate text-[13px] text-fg">{o.name}</span>}
                  <span class={`block truncate ${o.name ? "text-xs text-fg-3" : "text-[13px] text-fg"}`}>{o.address}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Chip({ r, onRemove, onEdit }: { r: Recipient; onRemove: () => void; onEdit: () => void }): JSX.Element {
  const parsed = r.name ? r : parseRecipient(r.raw);
  return (
    <span
      class={`inline-flex h-7 max-w-full items-center gap-1.5 rounded-full border pl-1 pr-1 text-[13px] ${
        parsed.valid ? "border-line bg-raised text-fg" : "border-danger/50 bg-danger/8 text-danger"
      }`}
      title={parsed.valid ? parsed.address : t("compose.invalidAddress")}
    >
      <Avatar address={parsed.address} label={parsed.name || parsed.address} size={20} />
      <button type="button" class="max-w-[220px] truncate" onClick={(e) => (e.stopPropagation(), onEdit())}>
        {parsed.name || parsed.address}
      </button>
      <button
        type="button"
        class="flex h-5 w-5 items-center justify-center rounded-full text-fg-3 hover:bg-sunken hover:text-fg"
        aria-label={t("compose.removeRecipient", { name: parsed.name || parsed.address })}
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
      >
        <Icon name="x" size={12} />
      </button>
    </span>
  );
}
